import { useEffect, useState } from "react";
import { ShieldAlert, Mail, Phone, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { popoverVariants } from "../../../utils/framerMotion";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getComplianceKycExpired } from "../../../store/admin/compliance-slice";
import { usePopover } from "../../../hooks/usePopover";
import { checkPermission } from "../../../utils/permissions";

const initials = (f?: string, s?: string) =>
  `${(f?.[0] ?? "").toUpperCase()}${(s?.[0] ?? "").toUpperCase()}` || "?";

/** Admin/Compliance navbar bell — clients whose KYC document expired today. */
const KycExpiredBell = () => {
  const dispatch = useAppDispatch();
  const { permissions } = useAppSelector((state) => state.auth);
  const { kycExpiredClients, isKycExpiredLoading } = useAppSelector(
    (state) => state.compliance,
  );
  const [open, setOpen] = useState(false);
  const ref = usePopover<HTMLDivElement>({ isOpen: open, onClose: () => setOpen(false) });

  const canView = checkPermission(permissions, "compliance", "canViewKyc");

  useEffect(() => {
    if (canView) dispatch(getComplianceKycExpired());
  }, [dispatch, canView]);

  if (!canView) return null;

  const count = kycExpiredClients.length;
  const animate = count > 0 && !open;

  return (
    <div className="relative" ref={ref}>
      <style>{`
        @keyframes admin-kyc-shake {
          0%, 55%, 100% { transform: rotate(0deg); }
          58% { transform: rotate(-10deg); }
          62% { transform: rotate(8deg); }
          66% { transform: rotate(-6deg); }
          70% { transform: rotate(4deg); }
          74% { transform: rotate(0deg); }
        }
        @keyframes admin-kyc-badge-pop {
          0%, 60%, 100% { transform: scale(1); }
          65% { transform: scale(1.25); }
          72% { transform: scale(0.92); }
          80% { transform: scale(1); }
        }
        .admin-kyc-shield-anim { animation: admin-kyc-shake 2.6s ease-in-out infinite; transform-origin: 50% 15%; }
        .admin-kyc-badge-anim { animation: admin-kyc-badge-pop 2.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .admin-kyc-shield-anim, .admin-kyc-badge-anim { animation: none; }
        }
      `}</style>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Clients whose KYC expired today"
        aria-label="Clients whose KYC expired today"
        className={`relative rounded-lg border px-2.5 py-1.5 transition-colors ${
          count > 0
            ? "border-rose-400/45 text-rose-300 hover:bg-rose-500/15"
            : "border-[#BBA473]/20 text-[#BBA473]/70 hover:bg-[#BBA473]/10 hover:text-[#BBA473]"
        }`}
      >
        <ShieldAlert size={18} className={animate ? "admin-kyc-shield-anim" : ""} />
        {count > 0 && (
          <span
            className={`absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white ${
              animate ? "admin-kyc-badge-anim" : ""
            }`}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      <AnimatePresence>
      {open && (
        <motion.div variants={popoverVariants} initial="hidden" animate="visible" exit="exit" className="absolute right-0 z-50 mt-2 w-80 origin-top-right overflow-hidden rounded-xl border border-[#BBA473]/20 bg-[#141009] shadow-[0_16px_48px_-12px_rgba(0,0,0,0.55)]">
          <div className="flex items-center justify-between border-b border-[#BBA473]/15 px-4 py-3">
            <div className="flex items-center gap-2">
              <ShieldAlert size={15} className="text-rose-300" />
              <span className="text-sm font-semibold text-[#F1EDE4]">
                KYC expired today
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-[#8A857A] hover:text-[#F1EDE4]"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {isKycExpiredLoading ? (
              <p className="px-4 py-6 text-center text-xs text-[#8A857A]">
                Loading…
              </p>
            ) : count === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-[#8A857A]">
                No client's KYC expired today.
              </p>
            ) : (
              <ul className="divide-y divide-[#BBA473]/10">
                {kycExpiredClients.map((c) => {
                  const digits = (c.phoneNumber || "").replace(/[^\d]/g, "");
                  const name = `${c.firstName ?? ""} ${c.surname ?? ""}`.trim();
                  return (
                    <li key={c.userId} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-rose-400/30 bg-rose-950/40 text-xs font-semibold text-rose-300">
                          {initials(c.firstName, c.surname)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-[#F1EDE4]">
                            {name || "—"}
                          </p>
                          <p className="truncate text-[11px] text-[#8A857A]">
                            {c.accountId}
                            {c.documentType ? ` · ${c.documentType}` : ""}
                          </p>
                        </div>
                      </div>
                      {(digits || c.email) && (
                        <div className="mt-2 flex items-center gap-1.5 pl-12">
                          {digits && (
                            <a
                              href={`tel:${c.phoneNumber}`}
                              title="Call"
                              className="rounded-md border border-[#BBA473]/20 p-1.5 text-[#ADA79A] hover:border-rose-400/40 hover:text-rose-300"
                            >
                              <Phone size={13} />
                            </a>
                          )}
                          {c.email && (
                            <a
                              href={`mailto:${c.email}?subject=${encodeURIComponent("Your KYC document has expired")}`}
                              title="Email"
                              className="rounded-md border border-[#BBA473]/20 p-1.5 text-[#ADA79A] hover:border-rose-400/40 hover:text-rose-300"
                            >
                              <Mail size={13} />
                            </a>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default KycExpiredBell;
